import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';

// Fixed label geometry for BookBridge — 50mm × 25mm die-cut, Zebra ZPL only.
// Printer tuning mirrors the reference implementation's defaults.
const LABEL_WIDTH_MM  = 50;
const LABEL_HEIGHT_MM = 25;
const DPI             = 203;
const DARKNESS        = 15;
const PRINT_SPEED     = 4;
const MEDIA_TYPE      = 'Y';   // gap/notch sensing
const BARCODE_MODULE_WIDTH = 2;

const DEVANAGARI_FONT = "'Noto Sans Devanagari', 'Mangal', Arial, sans-serif";

export interface PrintResult {
  printed: boolean;
  /** Set when printed is false — human-readable reason a .zpl file was downloaded instead. */
  reason?: string;
}

@Injectable({ providedIn: 'root' })
export class LabelPrintService {

  constructor(
    private api:  ApiService,
    private auth: AuthService,
  ) {}

  /** Last Browser Print base URL that answered — tried first on the next call. */
  private _bpWorkingBase: string | null = null;

  /**
   * Candidate Browser Print endpoints, preferred first. On an https page the
   * HTTPS port (9101) is preferred, but its self-signed certificate can lose
   * the browser's trust (ERR_CERT_AUTHORITY_INVALID) — so plain http on 9100 is
   * tried as well; Chrome/Edge/Firefox treat http://localhost as a secure
   * origin and allow it from https pages.
   */
  private get _bpBases(): string[] {
    const https = 'https://localhost:9101';
    const http  = 'http://localhost:9100';
    const ordered = window.location.protocol === 'https:' ? [https, http] : [http];
    if (this._bpWorkingBase && ordered.includes(this._bpWorkingBase)) {
      return [this._bpWorkingBase, ...ordered.filter(b => b !== this._bpWorkingBase)];
    }
    return ordered;
  }

  /**
   * fetch() against Browser Print, trying each candidate base until one is
   * reachable. Only network-level failures (cert rejected, service down) move
   * on to the next base; an HTTP response of any status is returned as-is.
   */
  private async _bpFetch(path: string, init?: RequestInit): Promise<Response> {
    let lastErr: any;
    for (const base of this._bpBases) {
      try {
        const res = await fetch(`${base}${path}`, init);
        this._bpWorkingBase = base;
        return res;
      } catch (err) {
        lastErr = err;
      }
    }
    this._bpWorkingBase = null;
    throw lastErr;
  }

  /**
   * Label's last line: sysParam "labelNameSource" customText overrides the
   * login-derived client name when present and non-blank; otherwise falls
   * back to the logged-in user's client name.
   */
  async resolveClientName(): Promise<string> {
    const loginDerived = this.auth.getCurrentUser()?.clientName || '';
    try {
      const cfg = await firstValueFrom(this.api.getLabelNameSource());
      const custom = (cfg?.customText || '').trim();
      return custom || loginDerived;
    } catch {
      return loginDerived;
    }
  }

  private dots(mm: number): number {
    return Math.round(mm * DPI / 25.4);
  }

  /**
   * Renders `text` onto a hidden canvas and encodes it as a ZPL ^GFA command.
   * The result is a monochrome bitmap the printer can render natively — this
   * is how Devanagari (and any Unicode script) gets printed on Zebra printers
   * that have no built-in Unicode font.
   */
  private async canvasTextToGFA(
    text: string,
    widthDots: number,
    heightDots: number,
    fontSize: number,
  ): Promise<string> {
    await document.fonts.load(`bold ${fontSize}px ${DEVANAGARI_FONT}`).catch(() => {});

    const canvas  = document.createElement('canvas');
    canvas.width  = widthDots;
    canvas.height = heightDots;
    const ctx = canvas.getContext('2d')!;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, widthDots, heightDots);

    ctx.fillStyle    = '#000000';
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';
    // Devanagari glyphs extend well beyond the em-square (shirorekha + matras
    // above, matras below) — sizing the font to the full box height clips
    // them at the canvas edge. Cap it below the box so there's headroom.
    let fs = Math.min(fontSize, Math.max(10, Math.round(heightDots * 0.72)));
    ctx.font = `bold ${fs}px ${DEVANAGARI_FONT}`;
    while (ctx.measureText(text).width > widthDots * 0.92 && fs > 10) {
      fs -= 2;
      ctx.font = `bold ${fs}px ${DEVANAGARI_FONT}`;
    }
    ctx.fillText(text, widthDots / 2, heightDots / 2);

    const { data: pixels } = ctx.getImageData(0, 0, widthDots, heightDots);
    const bytesPerRow = Math.ceil(widthDots / 8);
    let hex = '';

    for (let y = 0; y < heightDots; y++) {
      for (let b = 0; b < bytesPerRow; b++) {
        let byte = 0;
        for (let bit = 0; bit < 8; bit++) {
          const x = b * 8 + bit;
          if (x < widthDots) {
            const i = (y * widthDots + x) * 4;
            const grey = 0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2];
            if (grey < 128) byte |= (0x80 >> bit);
          }
        }
        hex += byte.toString(16).toUpperCase().padStart(2, '0');
      }
    }

    const total = bytesPerRow * heightDots;
    return `^GFA,${total},${total},${bytesPerRow},${hex}`;
  }

  /** Returns discovered Zebra printers via Browser Print REST API. Empty array if service not running. */
  async getAvailablePrinters(): Promise<any[]> {
    try {
      const res = await this._bpFetch('/available');
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data?.printer) ? data.printer : [];
    } catch {
      return [];
    }
  }

  /**
   * Builds one ZPL label per srNo: book name (top), barcode, sr no (below
   * barcode), client name (bottom). bookName/clientName bitmaps are shared
   * across the batch since only the srNo changes copy to copy.
   */
  async buildZpl(bookName: string, srNos: number[], clientName: string): Promise<string> {
    const wDots = this.dots(LABEL_WIDTH_MM);
    const hDots = this.dots(LABEL_HEIGHT_MM);
    const mX    = this.dots(2);
    const usableW = wDots - 2 * mX;

    const topPad    = this.dots(1.5);
    // Title/client boxes are taller than their rendered font size (see the
    // 0.72 cap in canvasTextToGFA) so Devanagari ascenders/descenders — the
    // shirorekha and matras — have room above/below the glyph instead of
    // being clipped at the canvas edge. The barcode has enough height slack
    // to absorb the difference (see barcodeH below).
    const titleFH   = this.dots(2.7);
    const gap       = this.dots(0.8);
    const srnoToClientGap = this.dots(1.6);
    const srnoFH    = this.dots(2.2);
    const clientFH  = this.dots(3.9);
    const bottomPad = this.dots(1);

    const titleGfa  = bookName   ? await this.canvasTextToGFA(bookName,   usableW, titleFH,  titleFH)  : null;
    const clientGfa = clientName ? await this.canvasTextToGFA(clientName, usableW, clientFH, clientFH) : null;

    const yTitle        = topPad;
    const yBarcodeStart = yTitle + (titleGfa ? titleFH + gap : 0);
    const yClient        = hDots - bottomPad - (clientGfa ? clientFH : 0);
    const ySrno          = yClient - (clientGfa ? srnoToClientGap : 0) - srnoFH;
    const barcodeH        = Math.max(this.dots(6), ySrno - gap - yBarcodeStart);

    const blocks = srNos.map(srNo => {
      const srPadded = String(srNo).padStart(6, '0');
      // Estimate CODE128 barcode width for horizontal centering
      const approxBarcodeWidth = Math.min(usableW, (srPadded.length * 11 + 35) * 2);
      const barcodeX = Math.max(mX, Math.round((wDots - approxBarcodeWidth) / 2));

      let zpl = `^XA\n^PW${wDots}\n^LL${hDots}\n^LS0\n^MD${DARKNESS}\n^PR${PRINT_SPEED}\n^MN${MEDIA_TYPE}\n`;

      if (titleGfa) zpl += `^FO${mX},${yTitle}${titleGfa}^FS\n`;

      zpl += `^BY${BARCODE_MODULE_WIDTH}\n`;
      zpl += `^FO${barcodeX},${yBarcodeStart}^BCN,${barcodeH},N,N,N^FD>:${srPadded}^FS\n`;
      zpl += `^CF0,${srnoFH}^FO${mX},${ySrno}^FB${usableW},1,0,C,0^FD${srPadded}^FS\n`;

      if (clientGfa) zpl += `^FO${mX},${yClient}${clientGfa}^FS\n`;

      zpl += `^PQ1\n^XZ`;
      return zpl;
    });

    return blocks.join('\n');
  }

  /**
   * Prints srNos as 50mm×25mm Zebra labels for a single book/client. Returns
   * whether the print actually reached the printer; when it didn't, a .zpl
   * file was downloaded as a fallback and `reason` explains why the local
   * Browser Print service couldn't be reached, so the operator isn't left
   * guessing why a "print" produced a file download instead.
   */
  async print(bookName: string, srNos: number[], clientName: string, device?: any): Promise<PrintResult> {
    if (!srNos.length) return { printed: false, reason: 'Nothing to print.' };
    const zpl = await this.buildZpl(bookName, srNos, clientName);
    return device ? this._sendZplToDevice(zpl, device) : this._sendToDefaultPrinter(zpl);
  }

  private async _sendToDefaultPrinter(zpl: string): Promise<PrintResult> {
    let device: any = null;
    let discoveryReason = '';
    try {
      const res = await this._bpFetch('/default?type=printer');
      if (res.ok) device = await res.json().catch(() => null);
      else discoveryReason = `Browser Print service responded with HTTP ${res.status}.`;
    } catch (err) {
      discoveryReason = this._describeUnreachable(err);
    }

    if (!device?.uid) {
      const available = await this.getAvailablePrinters();
      device = available[0] ?? null;
    }

    if (!device?.uid) {
      this.downloadZpl(zpl);
      return { printed: false, reason: discoveryReason || 'No Zebra printer found — is Browser Print running and the printer connected?' };
    }
    return this._sendZplToDevice(zpl, device);
  }

  private async _sendZplToDevice(zpl: string, device: any): Promise<PrintResult> {
    try {
      const res = await this._bpFetch('/write', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device, data: zpl }),
      });
      if (!res.ok) {
        this.downloadZpl(zpl);
        return { printed: false, reason: `Printer rejected the print job (HTTP ${res.status}).` };
      }
      return { printed: true };
    } catch (err) {
      this.downloadZpl(zpl);
      return { printed: false, reason: this._describeUnreachable(err) };
    }
  }

  /**
   * fetch() only ever rejects with a generic "Failed to fetch" TypeError — the
   * browser deliberately hides *why* (cert not trusted, service not running,
   * blocked by local security software) from script. The most common real-world
   * cause on the https:// page path is that Browser Print's local self-signed
   * certificate at https://localhost:9101 has lost the browser's trust, so we
   * name that as the first thing to check rather than just "couldn't connect".
   */
  private _describeUnreachable(err: any): string {
    const detail = err?.message || String(err);
    const bases = this._bpBases;
    const tried = bases.join(' and ');
    const httpsBase = bases.find(b => b.startsWith('https:'));
    if (httpsBase) {
      return `Could not reach Zebra Browser Print at ${tried} (${detail}). Open ${httpsBase}/available in a new browser tab — if it shows a certificate warning, accept it and try printing again; otherwise confirm Browser Print is running.`;
    }
    return `Could not reach Zebra Browser Print at ${tried} (${detail}). Confirm Browser Print is running.`;
  }

  /** Download ZPL as a file — fallback when Zebra Browser Print is not installed/reachable. */
  downloadZpl(zpl: string, filename = 'labels.zpl'): void {
    const blob = new Blob([zpl], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
